import React, { useState, useEffect } from 'react';
import './KnowledgeGraph.css';

const KnowledgeGraph = ({ analysisData, sessionId }) => {
  const [activeTab, setActiveTab] = useState('comparison');
  const [graphHtml, setGraphHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showComparisonPanel, setShowComparisonPanel] = useState(true);

  // Check data availability
  const hasOldData = analysisData?.old_kg_triplets?.length > 0;
  const hasNewData = analysisData?.new_kg_triplets?.length > 0;
  const hasComparisonData = hasOldData && hasNewData;

  // Fetch knowledge graph HTML from backend
  const fetchKnowledgeGraph = React.useCallback(async (graphType) => {
    console.log('=== KNOWLEDGE GRAPH DEBUG ===');
    console.log('sessionId:', sessionId);
    console.log('graphType:', graphType);
    console.log('analysisData:', analysisData);
    console.log('API Base URL:', process.env.REACT_APP_API_URL);
    
    if (!sessionId) {
      console.error('No session ID available');
      setError('No session ID available');
      return;
    }

    // Validate data availability before making request
    if (graphType === 'old' && !hasOldData) {
      setError('Original version data not available.');
      setLoading(false);
      return;
    }
    
    if (graphType === 'new' && !hasNewData) {
      setError('Modified version data not available.');
      setLoading(false);
      return;
    }
    
    if (graphType === 'comparison' && !hasComparisonData) {
      setError('Comparison view requires both original and modified version data. Modified version data is missing.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = `${process.env.REACT_APP_API_URL || ""}/api/sessions/${sessionId}/knowledge-graph?graph_type=${graphType}&phase=impact`;
      console.log('Fetching from URL:', url);
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error text:', errorText);
        
        // Try to parse error message for better UX
        let errorMessage = `Failed to fetch knowledge graph: ${response.status} ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          // Use default error message
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Response data:', data);
      console.log('HTML length:', data.html?.length);
      
      setGraphHtml(data.html);
    } catch (err) {
      console.error('Error fetching knowledge graph:', err);
      setError(err.message);
      setGraphHtml('');
    } finally {
      setLoading(false);
    }
  }, [sessionId, analysisData, hasOldData, hasNewData, hasComparisonData]);


  // Set initial tab to first available option
  useEffect(() => {
    if (analysisData) {
      if (activeTab === 'comparison' && !hasComparisonData) {
        // Fall back to old if comparison not available
        if (hasOldData) {
          setActiveTab('old');
        } else if (hasNewData) {
          setActiveTab('new');
        }
      } else if (activeTab === 'new' && !hasNewData) {
        // Fall back to old if new not available
        if (hasOldData) {
          setActiveTab('old');
        }
      } else if (activeTab === 'old' && !hasOldData) {
        // Fall back to new if old not available
        if (hasNewData) {
          setActiveTab('new');
        }
      }
    }
  }, [analysisData, activeTab, hasOldData, hasNewData, hasComparisonData]);

  // Load graph when tab changes
  useEffect(() => {
    if (analysisData && sessionId) {
      fetchKnowledgeGraph(activeTab);
    }
  }, [activeTab, analysisData, sessionId, fetchKnowledgeGraph]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const getTabLabel = (tab) => {
    switch (tab) {
      case 'old': return 'Original Version';
      case 'new': return 'Modified Version';
      case 'comparison': return 'Comparison View';
      default: return tab;
    }
  };

  // Function to inject close button into comparison panel
  const injectCloseButton = (html) => {
    if (!html || activeTab !== 'comparison') return html;
    
    // Add CSS for the close button
    const closeButtonCSS = `
      <style>
        .comparison-panel-close {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #e53e3e;
          color: white;
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          cursor: pointer;
          font-size: 14px;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          transition: all 0.2s ease;
        }
        .comparison-panel-close:hover {
          background: #c53030;
          transform: scale(1.1);
        }
        .comparison-panel {
          position: relative !important;
        }
      </style>
    `;
    
    let modifiedHtml = html;
    
    // Add CSS to head or body if no head exists
    if (modifiedHtml.includes('<head>')) {
      modifiedHtml = modifiedHtml.replace('<head>', `<head>${closeButtonCSS}`);
    } else if (modifiedHtml.includes('<body>')) {
      modifiedHtml = modifiedHtml.replace('<body>', `<body>${closeButtonCSS}`);
    } else {
      // If no head or body, add at the beginning
      modifiedHtml = closeButtonCSS + modifiedHtml;
    }
    
    // Find comparison panels and add close button
    // Look for divs with "comparison" in class name or containing "COMPARISON VIEW" text
    modifiedHtml = modifiedHtml.replace(
      /(<div[^>]*class="[^"]*comparison[^"]*"[^>]*>)/gi,
      (match) => {
        // Add relative positioning and close button
        const closeButton = '<button class="comparison-panel-close" onclick="this.parentElement.style.display=\'none\'">✖</button>';
        return match + closeButton;
      }
    );
    
    // Also look for panels containing "COMPARISON VIEW" text
    modifiedHtml = modifiedHtml.replace(
      /(<div[^>]*>[\s\S]*?COMPARISON VIEW[\s\S]*?<\/div>)/gi,
      (match) => {
        if (!match.includes('comparison-panel-close')) {
          const closeButton = '<button class="comparison-panel-close" onclick="this.parentElement.style.display=\'none\'">✖</button>';
          return match.replace('>', ' style="position: relative;">') + closeButton;
        }
        return match;
      }
    );
    
    return modifiedHtml;
  };

  const hasData = analysisData && (
    analysisData.old_kg_triplets?.length > 0 || 
    analysisData.new_kg_triplets?.length > 0
  );

  if (!hasData) {
    return (
      <div className="kg-container">
        <div className="kg-placeholder">
          <div className="kg-placeholder-icon">📊</div>
          <h3>No Knowledge Graph Data</h3>
          <p>Run the analysis to generate knowledge graph visualization</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kg-container">
      <div className="kg-header">
        <h3>Knowledge Graph Visualization</h3>
        <div className="kg-tabs">
          {['old', 'new', 'comparison'].map(tab => {
            const isAvailable = 
              (tab === 'old' && hasOldData) ||
              (tab === 'new' && hasNewData) ||
              (tab === 'comparison' && hasComparisonData);
            
            let tooltipText = '';
            if (!isAvailable) {
              if (tab === 'new') {
                tooltipText = 'Modified version data not available. Files were removed (not modified), so there is no new version to display.';
              } else if (tab === 'comparison') {
                tooltipText = 'Comparison view requires both original and modified version data. Since files were removed, there is no modified version to compare.';
              } else {
                tooltipText = `${getTabLabel(tab)} data not available`;
              }
            }
            
            return (
              <button
                key={tab}
                className={`kg-tab ${activeTab === tab ? 'active' : ''} ${!isAvailable ? 'disabled' : ''}`}
                onClick={() => isAvailable && handleTabClick(tab)}
                disabled={!isAvailable}
                title={tooltipText}
              >
                {getTabLabel(tab)}
                {!isAvailable && <span style={{opacity: 0.6, fontSize: '0.85em', marginLeft: '4px'}}>(N/A)</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="kg-content">
        {loading && (
          <div className="kg-loading">
            <div className="loading-spinner"></div>
            <p>Generating knowledge graph...</p>
          </div>
        )}

        {error && (
          <div className="kg-error">
            <div className="error-icon">⚠️</div>
            <h4>Error Loading Graph</h4>
            <p>{error}</p>
            <button 
              className="retry-button"
              onClick={() => fetchKnowledgeGraph(activeTab)}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && graphHtml && (
          <div className="kg-graph-container">
            <iframe 
              className="kg-graph-iframe"
              srcDoc={injectCloseButton(graphHtml)}
              title="Knowledge Graph"
              style={{
                width: '100%',
                height: '750px',
                border: 'none',
                borderRadius: '8px'
              }}
            />
          </div>
        )}

        {!loading && !error && !graphHtml && (
          <div className="kg-placeholder">
            <div className="kg-placeholder-icon">📊</div>
            <h4>No Data Available</h4>
            <p>No knowledge graph data available for {getTabLabel(activeTab)}</p>
            {activeTab === 'new' && (
              <p style={{marginTop: '8px', fontSize: '0.9em', color: '#94a3b8'}}>
                Modified version data is not available. This may occur when files were removed rather than modified.
              </p>
            )}
            {activeTab === 'comparison' && (
              <p style={{marginTop: '8px', fontSize: '0.9em', color: '#94a3b8'}}>
                Comparison view requires both original and modified version data. Modified version data is missing because files were removed.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeGraph;